import { useState, type ReactNode } from "react";
import { Link } from "wouter";
import { AlertTriangle, Inbox, RefreshCcw } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * QueryState — one place to render the loading / error / empty states around a
 * React Query fetch.
 *
 * Before this existed, 96 of 97 web pages rendered nothing (or "no results")
 * when the API failed: no explanation, no retry, silent churn. Wrap the render
 * of any query-backed list or detail page in this and the user always gets
 * something honest plus a way out.
 *
 *   const { data, isLoading, isError, refetch } = useGetDeals(...);
 *
 *   <QueryState
 *     isLoading={isLoading}
 *     isError={isError}
 *     isEmpty={deals.length === 0}
 *     onRetry={refetch}
 *     skeleton={<DealsSkeleton />}
 *     errorTitle="Couldn't load deals"
 *     emptyIcon="🏷️"
 *     emptyTitle="No deals available right now."
 *     emptyBody="Check back soon or try a different category."
 *     emptyAction={{ label: "See all Ayrshire deals", onClick: clearTown }}
 *   >
 *     {grid}
 *   </QueryState>
 */

export type QueryStateAction = {
  label: string;
  /** Internal route — rendered as a wouter <Link>. */
  href?: string;
  /** Click handler — rendered as a <button>. Ignored when `href` is set. */
  onClick?: () => void;
};

export interface QueryStateProps {
  /** React Query `isLoading` (first load, no data yet). */
  isLoading?: boolean;
  /** React Query `isError`. */
  isError?: boolean;
  /** The fetch succeeded but there is nothing to show. */
  isEmpty?: boolean;
  /** React Query `refetch`. Omit and no retry button is rendered. */
  onRetry?: () => unknown | Promise<unknown>;

  /** Page-specific loading skeleton. Falls back to a spinner. */
  skeleton?: ReactNode;

  errorTitle?: string;
  errorBody?: string;
  /** Optional secondary action on the error card (e.g. "Back to News"). */
  errorAction?: QueryStateAction;

  /** Emoji string or a lucide icon node. */
  emptyIcon?: ReactNode;
  emptyTitle?: string;
  emptyBody?: string;
  emptyAction?: QueryStateAction;
  /** Escape hatch: render this instead of the generic empty card. */
  empty?: ReactNode;

  /** Centre the card in a full-height page — for detail pages. */
  fullPage?: boolean;
  className?: string;

  children?: ReactNode;
}

/**
 * True when the failure was a 404 from the API client (`ApiError.status`).
 * Detail pages use this to keep their "not found" copy instead of telling the
 * user to retry something that will never succeed.
 */
export function isNotFoundError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { status?: unknown }).status === 404
  );
}

function ActionButton({ action, tone }: { action: QueryStateAction; tone: "primary" | "ghost" }) {
  const classes = cn(
    "inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors",
    tone === "primary"
      ? "bg-primary text-white hover:bg-primary/90"
      : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-primary",
  );

  if (action.href) {
    return (
      <Link href={action.href} className={classes}>
        {action.label}
      </Link>
    );
  }

  return (
    <button type="button" onClick={action.onClick} className={classes}>
      {action.label}
    </button>
  );
}

function Shell({
  fullPage,
  className,
  children,
}: {
  fullPage?: boolean;
  className?: string;
  children: ReactNode;
}) {
  if (fullPage) {
    return (
      <div className={cn("min-h-screen bg-slate-50 flex items-center justify-center px-4", className)}>
        {children}
      </div>
    );
  }
  return <div className={cn("py-20 px-4", className)}>{children}</div>;
}

export function QueryState({
  isLoading = false,
  isError = false,
  isEmpty = false,
  onRetry,
  skeleton,
  errorTitle = "Something went wrong",
  errorBody = "We couldn't load this right now. It's usually a temporary connection problem — give it another go.",
  errorAction,
  emptyIcon,
  emptyTitle = "Nothing here yet",
  emptyBody,
  emptyAction,
  empty,
  fullPage = false,
  className,
  children,
}: QueryStateProps) {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    if (!onRetry || retrying) return;
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  if (isLoading) {
    if (skeleton) return <>{skeleton}</>;
    return (
      <Shell fullPage={fullPage} className={className}>
        <div className="flex flex-col items-center gap-3 text-center" role="status" aria-live="polite">
          <RefreshCcw size={22} className="text-primary animate-spin" />
          <p className="text-sm text-slate-400">Loading…</p>
        </div>
      </Shell>
    );
  }

  if (isError) {
    return (
      <Shell fullPage={fullPage} className={className}>
        <div className="mx-auto max-w-sm text-center" role="alert">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={28} className="text-red-500" />
          </div>
          <h3 className="text-lg font-bold text-slate-800">{errorTitle}</h3>
          <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{errorBody}</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            {onRetry && (
              <button
                type="button"
                onClick={handleRetry}
                disabled={retrying}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCcw size={15} className={retrying ? "animate-spin" : undefined} />
                {retrying ? "Retrying…" : "Try again"}
              </button>
            )}
            {errorAction && <ActionButton action={errorAction} tone={onRetry ? "ghost" : "primary"} />}
          </div>
        </div>
      </Shell>
    );
  }

  if (isEmpty) {
    if (empty) return <>{empty}</>;
    return (
      <Shell fullPage={fullPage} className={className}>
        <div className="mx-auto max-w-sm text-center">
          {typeof emptyIcon === "string" ? (
            <p className="text-5xl mb-4">{emptyIcon}</p>
          ) : emptyIcon ? (
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              {emptyIcon}
            </div>
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Inbox size={28} className="text-slate-400" />
            </div>
          )}
          <h3 className="text-lg font-bold text-slate-800">{emptyTitle}</h3>
          {emptyBody && <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{emptyBody}</p>}
          {emptyAction && (
            <div className="mt-6 flex justify-center">
              <ActionButton action={emptyAction} tone="primary" />
            </div>
          )}
        </div>
      </Shell>
    );
  }

  return <>{children}</>;
}

export default QueryState;
